/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { toast } from 'react-toastify';
import { closestCorners, DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { InspectorWidgetLayout } from '@/features/editor/widgets/shared/inspector-widget-layout';
import { BigButton } from '@/components/big-button';
import { EditorContext } from '@/features/editor/context/editor-context';
import { DeviceType } from '@/utils/definitions';
import { VSpace } from '@/components/spacing';
import { Flexbox } from '@/components/flex';
import { CustomWidgetContext } from '@/features/editor/widgets/custom-widget/shared/context';
import { DropdownInput } from '@/features/editor/widgets/custom-widget/inputs/shared/dropdown';
import { InputsDarkTheme } from '@/features/editor/widgets/custom-widget/inputs/shared/input-type';
import {
  MaybeFiltered,
  Section,
  SectionTitle,
} from '@/features/editor/widgets/custom-widget/shared/shared-styles';
import { LoadingSection } from '@/features/editor/widgets/custom-widget/loading-section';

import { extractQuickEditItems } from '@/features/editor/widgets/custom-widget/shared/quick-edit-helper';
import { QuickEditItem } from '@/features/editor/widgets/custom-widget/inputs/shared/quick-edit-item';
import { filterRecommendations } from '@/features/editor/widgets/custom-widget/loading-section/recs-per-placement';
import { INSPECTOR_BACK_ID } from '@/features/editor/inspector';
import { INPUT_BACKGROUND_CLASS } from '@/features/editor/widgets/custom-widget/inputs/background';
import { INPUT_LINK_CLASS } from '@/features/editor/widgets/custom-widget/inputs/shared/link';
import {
  CARD_WRAPPER_CONTENT,
  CARD_WRAPPER_HEADER,
} from '@/features/editor/widgets/custom-widget/inputs/shared/card-wrapper';
import { getRecStrategy, Spec } from '@/webapi/use-widget-catalog-api';
import { ReviewsModal } from '@/features/integrations/reviews-modal';
import { UndoRedoBtns } from '@/components/undo-redo-btns';
import { GlossyWrapper } from '@/components/glossy-wrapper';
import {
  newUndoRedoContext,
  UndoRedoCounterContext,
} from '@/features/editor/widgets/custom-widget/shared/undo-redo-counter-context';
import {
  BlueprintOnboarding,
  useBlueprintOnboarding,
} from '@/features/editor/widgets/custom-widget/onboard';
import { QuickEditGroups } from '@/features/editor/widgets/custom-widget/shared/quick-edit-groups';
import { currencyValue } from '@/utils/currencies';
import { CustomizeDesignBtn } from '@/features/editor/widgets/custom-widget/style-templating/customize-design-btn';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

export function BlueprintCustomize({ updateFlow }: { updateFlow?: boolean }) {
  const {
    devicePreview: {
      leaveAnchor,
      editorState: { device },
    },
    experienceState: { upsertEditorChange, currentExperience },
    inspectorNav: { gotoChangelog },
    transpiler: { asWidgetChange },
    resources: { reviews },
    revertTempChange,
  } = useContext(EditorContext);

  const [isReviewsVisible, setIsReviewsVisible] = useState(false);

  const {
    gotoStyle,
    newChange,
    currentWidget,
    evaluateRule,
    setGeneralCustomization,
    patchGeneralSpecs,
    currentSchema,
    updateLoadingCfg,
    gotoSelect,
    undo,
    redo,
    canUndo,
    canRedo,
    undoRedoCount,
    styleTemplateChange,
    swapCustomization,
    addBlockAfter,
    addBlockBefore,
    filterDisabledCustomizations,
  } = useContext(CustomWidgetContext);

  const {
    settings: { general, loading },
  } = currentSchema;
  const isSimilarProductsEnabled = useFeatureBit(FeatureBit.SIMILAR_PRODUCTS);
  const recStrategies = filterRecommendations(
    currentExperience?.quickPreviewPlacement,
    loading,
    isSimilarProductsEnabled,
  );

  const hasQuickEditGroups =
    currentSchema?.settings?.quickEditGroups !== undefined;

  const quickEdit = useMemo(
    () => Object.entries(extractQuickEditItems(currentSchema)),
    [currentSchema],
  );

  const onEditStyle = () => {
    gotoStyle(currentWidget);
  };

  function validateSchema() {
    let hasValidImage = true;
    currentSchema.customizations.forEach((cust) => {
      cust.components.forEach((comp) => {
        comp.specs.forEach((spec) => {
          const isImage = spec?.values?.[`mode`] === `image`;
          const value = spec?.values?.[`value`];
          const isBlob = value?.includes ? value?.includes(`blob`) : false;
          if (isImage && isBlob) {
            hasValidImage = false;
            toast(
              `There is a problem with your image, please re-upload it and try again`,
              {
                theme: `colored`,
                type: `error`,
                className: css({
                  fontFamily: `JetBrains Mono, Serif`,
                  fontWight: `700`,
                }),
              },
            );
          }
        });
      });
    });
    return hasValidImage;
  }

  const onSave = async () => {
    const isValid = validateSchema();

    if (isValid) {
      const finalChange = asWidgetChange(
        newChange,
        currentWidget,
        currentSchema,
      );
      upsertEditorChange(finalChange);
      leaveAnchor();
      gotoChangelog();
    }
  };

  const onBack = () => {
    if (updateFlow) {
      gotoChangelog();
    } else {
      revertTempChange();
      gotoSelect();
    }
  };

  useEffect(() => {
    if (currentSchema?.settings?.loading?.value) {
      updateLoadingCfg(currentSchema?.settings?.loading?.value);
    }
  }, []);

  useEffect(() => {
    if (updateFlow) {
      patchGeneralSpecs();
    }
  }, []);

  const getGeneralValue = useCallback(
    (specIdx: number) => {
      const spec = general?.specs?.[specIdx];

      const val =
        device === DeviceType.Mobile
          ? spec.value
          : spec.value?.desktop || spec.value;
      if (
        spec.key === `currency` &&
        val?.value === `default free shipping goal`
      ) {
        const options = spec.metadata.options as Array<{
          value: string;
          label: string;
        }>;
        return {
          val:
            options.find((o) => o.value === currencyValue()) ||
            ({
              value: `USD`,
              label: `Goal Currency - $ USD`,
            } as any),
          isDefaultOverride: true,
        };
      }
      return { val, isDefaultOverride: false };
    },
    [device, undoRedoCount, styleTemplateChange],
  );

  const wrapperRef = useRef(null);
  const onGeneralSettingsClicked = (ev: any, spec: Spec) => {
    if (spec.key === `reviewsPlacement` && !reviews.reviewsEnabled) {
      ev.stopPropagation();
      ev.preventDefault();
      setIsReviewsVisible(true);
    }
  };

  const hiddenSpecs = useMemo(() => {
    if (!evaluateRule) return [];
    return general?.specs
      ?.filter((spec) => evaluateRule(spec?.hideWhen))
      ?.map((spec) => spec.key);
  }, [general?.specs, device]);

  const isDefault = getRecStrategy(currentSchema)?.default;
  const undoRedoContext = newUndoRedoContext(undoRedoCount);

  const onboardStep2Ref = useRef(undefined);
  const onboardStep3Ref = useRef(undefined);
  const { shouldRunOnboarding } = useBlueprintOnboarding(newChange.editorId);

  const isAllowToDragAccount = useFeatureBit(
    FeatureBit.DRAGGABLE_MULTIBLOCKS_WIDGET,
  );

  const hasDraggableItems = useMemo(
    () =>
      isAllowToDragAccount &&
      quickEdit?.some(([, v]) => v[0]?.customization?.draggable),
    [],
  );

  const { firstDisabledIdx, firstEnabledIdx } = useMemo(() => {
    if (!hasDraggableItems) {
      return {
        firstDisabledIdx: -1,
        firstEnabledIdx: -1,
      };
    }

    const draggableItems = quickEdit.filter(
      ([, v]) => v[0]?.customization?.draggable,
    );

    const firstDisabledIdx =
      draggableItems.find(
        ([, v]) =>
          v[0]?.customization?.isDisabled &&
          v[0]?.customizationIdx !== undefined,
      )?.[1]?.[0]?.customizationIdx ?? -1;

    const firstEnabledIdx =
      draggableItems.find(
        ([, v]) =>
          !v[0]?.customization?.isDisabled &&
          v[0]?.customizationIdx !== undefined,
      )?.[1]?.[0]?.customizationIdx ?? -1;

    return {
      firstDisabledIdx,
      firstEnabledIdx,
    };
  }, [quickEdit]);

  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const [orderKey, setOrderKey] = useState(0);
  const [animateChanges, setAnimateChanges] = useState(false);

  const activeQuickEdit = useMemo(() => {
    if (!activeDragId) return null;
    return quickEdit.find(([k]) => k === activeDragId);
  }, [activeDragId, quickEdit]);

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };
  const handleDragEnd = (event) => {
    setActiveDragId(null);

    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;
    const activeEntry = quickEdit.find(([k]) => k === active.id);
    const overEntry = quickEdit.find(([k]) => k === over.id);
    if (!activeEntry || !overEntry) return;
    const activeIdx = activeEntry[1]?.[0]?.customizationIdx;
    const overIdx = overEntry[1]?.[0]?.customizationIdx;
    if (activeIdx === undefined || overIdx === undefined) return;
    if (activeIdx === overIdx) return;
    swapCustomization(activeIdx, overIdx);
    setOrderKey((prev) => prev + 1);
  };
  const handleDragCancel = () => {
    setActiveDragId(null);
  };

  const addAnimation = () => {
    setAnimateChanges(true);
    setOrderKey((prev) => prev + 1);
    setTimeout(() => {
      setAnimateChanges(false);
    }, 500 + 100);
  };

  const addBlockAfterClick = () => {
    addBlockAfter(firstDisabledIdx);
    addAnimation();

    setTimeout(() => {
      if (onboardStep2Ref?.current) {
        const parent = onboardStep2Ref.current.closest(`#vslyGradientScroller`);
        if (parent) {
          parent.scrollTo({
            top: parent.scrollHeight,
            left: 0,
            behavior: `smooth`,
          });
        } else {
          onboardStep2Ref.current.scrollIntoView({
            behavior: `smooth`,
            block: `end`,
          });
        }
      }
    }, 500 + 100);
  };
  const addBlockBeforeClick = () => {
    addBlockBefore(firstDisabledIdx);
    addAnimation();
  };

  useEffect(() => {
    if (hasDraggableItems) {
      filterDisabledCustomizations();
      setOrderKey((prev) => prev + 1);
    }
  }, []);

  return (
    <UndoRedoCounterContext.Provider value={undoRedoContext}>
      <Wrapper ref={wrapperRef} device={device}>
        <ReviewsModal
          fromRef={wrapperRef}
          isVisible={isReviewsVisible}
          setIsVisible={setIsReviewsVisible}
        />
        <InspectorWidgetLayout
          progress={80}
          progressWidthPercentMobile={55}
          title={`3. All great, let's customize`}
          backCaption={
            updateFlow ? `< Back to experience` : `< Back to selection`
          }
          onBackClicked={onBack}
          footer={
            <FooterSection>
              <UndoRedoBtns
                redo={redo}
                undo={undo}
                canUndo={canUndo}
                canRedo={canRedo}
              />
              <BigButton
                disabled={isDefault}
                color="white"
                background="black"
                noTransform
                fillWidth
                size={device === DeviceType.Desktop ? `medium-thin` : `medium`}
                onClick={onSave}
              >
                {updateFlow ? `Update` : `Save`}
              </BigButton>
            </FooterSection>
          }
        >
          <CustomizeSection direction="column">
            <CustomizeDesignBtn onClick={onEditStyle} />
            {!!general && general.specs.length > 0 && (
              <>
                <Group>
                  {shouldRunOnboarding && (
                    <BlueprintOnboarding
                      zIndex={10}
                      maxHeight="30rem"
                      delay={100}
                      title="General Customization"
                      hint="In this section, you can modify the general settings"
                    />
                  )}
                  <SectionTitle>General Customization</SectionTitle>
                  <Section>
                    {general.specs.map((spec, specIdx) => {
                      const visible =
                        !spec.hidden && !hiddenSpecs?.includes(spec.key);
                      const { val: generalValue, isDefaultOverride } =
                        getGeneralValue(specIdx);
                      if (isDefaultOverride) {
                        setTimeout(() => {
                          setGeneralCustomization(specIdx, generalValue);
                        }, 100);
                      }
                      return (
                        <MaybeFiltered
                          isVisible={visible}
                          onClick={(ev) => onGeneralSettingsClicked(ev, spec)}
                        >
                          <DropdownInput
                            key={`${spec.key}-${device}-${undoRedoCount}-${styleTemplateChange}`}
                            theme={InputsDarkTheme}
                            options={spec.metadata[`options`]}
                            defaultValue={generalValue}
                            onChange={(val) =>
                              setGeneralCustomization(specIdx, val)
                            }
                            optionStyles={{
                              textTransform: `uppercase`,
                              fontFamily: `Inter, serif`,
                            }}
                            containerStyles={{
                              fontFamily: `Inter, serif`,
                              lineHeight: `2.1`,
                            }}
                          />
                        </MaybeFiltered>
                      );
                    })}
                  </Section>
                </Group>
                <VSpace value={3} />
              </>
            )}

            <Group ref={onboardStep2Ref}>
              {shouldRunOnboarding && (
                <BlueprintOnboarding
                  maxHeight="20rem"
                  zIndex={9}
                  delay={general ? 3000 : 100}
                  title="Quick Edit"
                  hint="In this section, you can modify the look & feel"
                />
              )}
              <SectionTitle>Quick Edit</SectionTitle>
              <StylingSection isQuickEdit>
                <QuickEditSection key={`qe${styleTemplateChange}`}>
                  {hasDraggableItems ? (
                    <DndContext
                      collisionDetection={closestCorners}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onDragCancel={handleDragCancel}
                    >
                      <SortableContext items={quickEdit.map(([k]) => k)}>
                        {quickEdit.map(([k, v]) => {
                          const idx = v[0]?.customizationIdx;

                          if (firstDisabledIdx === 0 && idx === 0)
                            return (
                              <AddBlock onClick={addBlockAfterClick}>
                                +Add Another Block
                              </AddBlock>
                            );

                          if (v[0]?.customization?.isDisabled) {
                            return null;
                          }
                          return (
                            <>
                              {idx === firstEnabledIdx &&
                                firstDisabledIdx >= 0 && (
                                  <AddBlock onClick={addBlockBeforeClick}>
                                    +Add Another Block
                                  </AddBlock>
                                )}
                              <QuickEditItem
                                key={`${k}${orderKey}`}
                                quickEdit={v}
                                device={device}
                                isAllowToDrag={v[0]?.customization?.draggable}
                                name={k}
                                setOrderKey={setOrderKey}
                                isAnimated={
                                  animateChanges &&
                                  (idx === firstDisabledIdx - 1 ||
                                    idx === firstEnabledIdx)
                                }
                              />
                              {idx === firstDisabledIdx - 1 && (
                                <AddBlock onClick={addBlockAfterClick}>
                                  +Add Another Block
                                </AddBlock>
                              )}
                            </>
                          );
                        })}
                      </SortableContext>
                      <DragOverlay>
                        {activeQuickEdit ? (
                          <QuickEditItem
                            isOverlayDND
                            quickEdit={activeQuickEdit[1]}
                            device={device}
                            isAllowToDrag
                            name={activeQuickEdit[0]}
                          />
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  ) : (
                    <>
                      {quickEdit.map(([k, v]) => (
                        <QuickEditItem key={k} quickEdit={v} device={device} />
                      ))}
                    </>
                  )}
                  {hasQuickEditGroups && (
                    <QuickEditGroups
                      groups={currentSchema?.settings?.quickEditGroups}
                    />
                  )}
                </QuickEditSection>
              </StylingSection>
            </Group>

            {!!recStrategies?.options && (
              <>
                <VSpace value={3} />
                <Group ref={onboardStep3Ref}>
                  {shouldRunOnboarding && (
                    <BlueprintOnboarding
                      zIndex={8}
                      maxHeight="15rem"
                      delay={general ? 6000 : 3000}
                      title="Which products do you want to add?"
                      hint="In this section, you can choose which products do you want to show"
                    />
                  )}
                  <LoadingSection
                    recStrategies={recStrategies}
                    device={device}
                  />
                </Group>
              </>
            )}
            <VSpace value={3} />
          </CustomizeSection>
        </InspectorWidgetLayout>
      </Wrapper>
    </UndoRedoCounterContext.Provider>
  );
}

export const Group = styled.section`
  width: 100%;
  position: relative;
  overflow-y: visible;
`;

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: ${(props: { device: DeviceType }) =>
    props.device === DeviceType.Desktop ? `100%` : `65%`};

  display: flex;
  flex-direction: column;
`;

const AddBlock = styled.div`
  display: flex;
  padding: 1.75rem 2rem;
  color: #798794;
  font-size: 1.4rem;
  cursor: pointer;
  background: #f4f6f7;
  border-radius: 10px;
  font-weight: 600;

  &&:hover {
    background: #e4e6e8;
  }
`;

export const CustomizeSection = styled(Flexbox)`
  && {
    overflow-y: scroll;
    scrollbar-width: none;

    #${INSPECTOR_BACK_ID} {
      z-index: 31;
    }
  }
`;

const FooterSection = styled(GlossyWrapper)`
  && {
    display: grid;
    grid-template-columns: 0.5fr 2fr;

    grid-gap: 2rem;
    z-index: 50;
    margin-left: -2rem;
    margin-bottom: 2rem;
  }
`;

export const StylingSection = styled(Section)`
  && {
    padding: ${(props: { isQuickEdit: boolean }) =>
      props.isQuickEdit ? `0` : `1rem 2rem 2rem 2rem`};
    margin-top: ${(props: { isQuickEdit: boolean }) =>
      props.isQuickEdit ? `unset` : `1.3rem`};
    background: ${(props: { isQuickEdit: boolean }) =>
      props.isQuickEdit ? `transparent` : `#f4f6f7`};
    border-radius: 10px;

    gap: ${(props: { isQuickEdit: boolean }) =>
      props.isQuickEdit ? `1rem` : `unset`};
    display: flex;
    align-items: center;
  }
`;

export const QuickEditSection = styled(Section)`
  && {
    && > :not(:last-child) {
      margin-bottom: 2rem;
    }

    .${INPUT_BACKGROUND_CLASS} {
      .${CARD_WRAPPER_HEADER} {
        border-bottom: unset !important;
      }
      .${CARD_WRAPPER_CONTENT} {
        display: none;
      }
    }

    .${INPUT_BACKGROUND_CLASS} + .${INPUT_LINK_CLASS} {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      margin-top: 0;
    }
  }
`;
